// Loven: small global behaviours layered on top of Dawn.

// Scrolls so the target sits just below the sticky header. Scrolling up makes Dawn's
// header slide back in, so its height is always reserved when it is sticky.
function lovenScrollTo(target) {
  const header = document.querySelector('.section-header');
  const sticky = header && getComputedStyle(header).position === 'sticky';
  const offset = sticky ? header.getBoundingClientRect().height : 0;
  const top = target.getBoundingClientRect().top + window.scrollY - offset - 16;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top: Math.max(top, 0), behavior: reduceMotion ? 'auto' : 'smooth' });
}

// Smooth-scroll for in-page links. Links that point at a section of a specific
// template (e.g. "#shopify-section-template--123__main") still work after the
// template is re-created, because the section key after "__" is matched instead.
document.addEventListener('click', (event) => {
  const link = event.target.closest('a[href^="#"]');
  if (!link) return;

  const hash = link.getAttribute('href');
  if (hash.length < 2) return;

  let target = null;
  try {
    target = document.querySelector(hash);
  } catch (e) {
    target = null;
  }

  if (!target && hash.includes('__')) {
    const sectionKey = hash.split('__').pop();
    target = document.querySelector(`[id$="__${CSS.escape(sectionKey)}"]`);
  }

  if (!target && (hash === '#main-product' || hash.endsWith('__main'))) {
    target = document.querySelector('[id^="MainProduct-"]');
  }

  if (!target) return;

  event.preventDefault();
  lovenScrollTo(target);
});

// Sticky add to cart bar on product pages.
if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        // Live directly under <body> so content from later sections (custom code with its own
        // z-index) can never paint over the bar. Moving it re-runs connectedCallback.
        if (this.parentElement !== document.body) {
          document.querySelectorAll(`body > sticky-atc[data-section="${this.dataset.section}"]`).forEach((old) => old.remove());
          document.body.appendChild(this);
          return;
        }
        this.sectionId = this.dataset.section;
        this.productForm = document.getElementById(`product-form-${this.sectionId}`);
        this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.button = this.querySelector('.sticky-atc__button');
        this.select = this.querySelector('select');

        this.button?.addEventListener('click', this.onButtonClick.bind(this));
        this.select?.addEventListener('change', this.onSelectChange.bind(this));

        const watched = this.mainButton || this.productForm;
        if (watched && 'IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            ([entry]) => {
              const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              this.toggle(passed);
            },
            { threshold: 0 }
          );
          this.observer.observe(watched);
        } else {
          this.toggle(true);
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data.sectionId !== this.sectionId) return;
            this.updateFromVariant(event.data.variant);
          });
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.unsubscribe?.();
      }

      toggle(visible) {
        this.classList.toggle('is-visible', visible);
        document.body.classList.toggle('sticky-atc-visible', visible);
        this.setAttribute('aria-hidden', String(!visible));
        if (!visible) this.button?.setAttribute('tabindex', '-1');
        else this.button?.removeAttribute('tabindex');
      }

      onButtonClick(event) {
        event.preventDefault();
        if (this.dataset.action === 'add_to_cart' && this.mainButton && !this.mainButton.disabled) {
          this.mainButton.click();
          return;
        }
        const target = document.getElementById(`ProductInfo-${this.sectionId}`) || this.productForm;
        if (target) lovenScrollTo(target);
      }

      onSelectChange() {
        const input = this.productForm?.querySelector('input[name="id"]');
        if (!input) return;
        input.value = this.select.value;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        const option = this.select.selectedOptions[0];
        const price = this.querySelector('.sticky-atc__price');
        if (price && option?.dataset.price) price.innerHTML = option.dataset.price;
      }

      updateFromVariant(variant) {
        if (!variant) return;
        if (this.select) this.select.value = variant.id;
        const option = this.select?.selectedOptions[0];
        const price = this.querySelector('.sticky-atc__price');
        if (price && option?.dataset.price) price.innerHTML = option.dataset.price;
        if (this.button && this.dataset.action === 'add_to_cart') {
          this.button.disabled = !variant.available;
        }
      }
    }
  );
}

// Estimated delivery dates on product pages.
(() => {
  const addDays = (start, days, skipWeekends) => {
    const date = new Date(start);
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (skipWeekends && (day === 0 || day === 6)) continue;
      added++;
    }
    return date;
  };

  const format = (date, pattern, locale) => {
    const options = { month: 'long', day: 'numeric' };
    if (pattern.startsWith('day_')) options.weekday = 'long';
    let lang = locale || document.documentElement.lang || 'en';
    if (pattern.endsWith('dd_mm') && lang.startsWith('en')) lang = 'en-GB';
    if (pattern.endsWith('mm_dd') && lang.startsWith('en')) lang = 'en-US';
    try {
      return new Intl.DateTimeFormat(lang, options).format(date);
    } catch (e) {
      return date.toDateString();
    }
  };

  const render = () => {
    document.querySelectorAll('[data-estimated-shipping]:not([data-rendered])').forEach((el) => {
      const skip = el.dataset.skipWeekends === 'true';
      const today = new Date();
      const start = addDays(today, parseInt(el.dataset.minDays, 10) || 0, skip);
      const end = addDays(today, parseInt(el.dataset.maxDays, 10) || 0, skip);
      const message = el.querySelector('.estimated-shipping__message');
      if (!message) return;
      message.innerHTML = message.innerHTML
        .replaceAll('[start_date]', format(start, el.dataset.dateFormat, el.dataset.locale))
        .replaceAll('[end_date]', format(end, el.dataset.dateFormat, el.dataset.locale));
      el.setAttribute('data-rendered', '');
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', render);
  } else {
    render();
  }
  document.addEventListener('shopify:section:load', render);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, () => setTimeout(render, 0));
  }
})();

// Remove a sticky bar moved to <body> when its product section is removed in the theme editor.
document.addEventListener('shopify:section:unload', (event) => {
  document.querySelectorAll(`body > sticky-atc[data-section="${event.detail.sectionId}"]`).forEach((el) => el.remove());
});
